import { Colors, Icon, TooltipProvider } from '@amelio/ui-library';

import { AvatarStack, type AvatarStackProps } from '@/components/AvatarStack/AvatarStack';
import { FeedbackSettingPill } from '@/components/FeedbackSettingsPills/FeedbackSettingsPills';
import {
    ANONYMOUS_FEEDBACK_MINIMUM_RESPONSES,
    FEEDBACK_PRIVACY_LABEL_KEYS,
    FeedbackPrivacy,
} from '@/constants/review/FeedbackPrivacy';
import { TOOLTIP_DELAY_MS } from '@/constants/Tooltip';
import { useAmelioTranslations } from '@/hooks';

interface AnonymousReviewersProps {
    isAnonymous: true;
    /** Explains the lock as the anonymous pill does; left out inside a card that is itself a link. */
    showTooltip?: boolean;
    dataTestId?: string;
}

type ReviewersStackProps = AnonymousReviewersProps | ({ isAnonymous: false } & AvatarStackProps);

const AnonymousReviewers = ({
    showTooltip = false,
    dataTestId = 'reviewers-stack',
}: AnonymousReviewersProps) => {
    const { t } = useAmelioTranslations();

    const lock = (
        <Icon
            name={'security-lock'}
            size={'sm'}
            color={Colors.BASE_400}
            ariaLabel={t(FEEDBACK_PRIVACY_LABEL_KEYS[FeedbackPrivacy.ANONYMOUS])}
        />
    );

    if (!showTooltip) {
        return lock;
    }

    return (
        <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
            <FeedbackSettingPill
                dataTestId={`${dataTestId}-lock`}
                explanation={[
                    t('evaluation360ScheduleCreation.resultsVisibility.noIndividualAnswer', {
                        minimum: ANONYMOUS_FEEDBACK_MINIMUM_RESPONSES,
                    }),
                ]}
            >
                {lock}
            </FeedbackSettingPill>
        </TooltipProvider>
    );
};

/**
 * The faces of a feedback's reviewers, or a lock in their place when the feedback is anonymous. The lock says the
 * feedback is anonymous, the way its pill does; the count shown beside it says how many reviewers there are.
 */
export const ReviewersStack = (props: ReviewersStackProps) =>
    props.isAnonymous ? <AnonymousReviewers {...props} /> : <AvatarStack {...props} />;
